// previewFrame — 編集画面の範囲プレビュー (修正3、growth-timelapse-share
// -2026-07-02.md §5.2 「範囲選択時にプレビューを見たい」)。
//
// 編集1画面に「選択範囲の始点フレームと終点フレームの2枚」を小さく表示するための
// **薄いラッパ**。`createTimelapseFrameSource` (T1 replay) を単発 pull して、
// straight-α RGBA 1枚を返すだけ。
//
//   - 始点プレビュー = 動画の frame 0 相当 (= 選択範囲の seed/leading-run が
//     適用された直後、実描画はごく僅かの状態)
//   - 終点プレビュー = 選択範囲を全部適用した完成形 (= 動画の最終フレーム相当)
//
// fix/timelapse-background (2026-09-27) で **動画生成と同じ入力** (全量ログ +
// `buildTimelapseSeqRanges` 相当の seqRanges) を渡すように変更した。以前は
// モーダルの仮想列 (`domain.events`) を「index 0 から untilIndex まで」で
// 都度切り詰めていたが、これだと境界イベント自身の直後にある土台 (leading run —
// 全消し直後の `whiteboard.background-set` 等) が **その切り詰めの `toSeq` で
// 遮られて土台に取り込まれない** バグがあった (`createTimelapseFrameSource` の
// leading-run 検出は `visible.toSeq` を越えて探さない — これは restore 系譜の
// 「snapshot 後の描き足しを混ぜない」不変条件のために必要な制約なので、
// leading-run 側を直さず、呼び出し側が正しい (動画と同じ) 範囲を渡すことで
// 解決する)。全量ログを渡すことで背景色の継承 (`backgroundAt`) も
// `createTimelapseFrameSource` 内部の既定計算に任せられる (= モーダル側で
// 別途 `backgroundAt` を計算して渡す二重実装をやめた)。
//
// **encode 層 (framePresenter / mp4Sink = OffscreenCanvas/WebCodecs) は通さない**。
// replay の `frame.rgba` をそのまま返す (= 本番の動画生成パスとは独立)。負荷対策:
//   - 縮小表示 (maxEdge を小さく) で per-frame composite / downscale を軽くする
//   - 終点は **単一フレームの plan** (duration 極小秒 → frameCount=1) で選択範囲
//     全体を適用した合成 1回だけ pull する (T1 の per-frame composite が律速)
//   - 始点は複数フレーム plan を組むが、generator は 1 個 pull したら即
//     `return()` して止める (= frame 0 の cutoff 分の events + composite 1回
//     だけのコスト。900 フレーム分の composite は発生しない)
//   - crop はかけない (§5.2「まず crop 無しの全体プレビューで可」)
//
// この層は T1/T2 の replay/encode 内部を**呼ぶだけ**で改変しない。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { CropRect } from './cropBox.js';
import { DEFAULT_TIMELAPSE_FPS } from './framePlan.js';
import type { FrameRgba, FrameSize } from './frameScale.js';
import type { SeqRange } from './segmentMerge.js';
import { createTimelapseFrameSource, type ResolveImagepasteFn } from './timelapseReplay.js';

/** プレビュー1枚の合成結果 (straight-α RGBA、`width × height`)。 */
export interface TimelapsePreviewFrame {
  readonly width: number;
  readonly height: number;
  readonly rgba: FrameRgba;
}

/** プレビューの既定長辺上限 (px)。縮小表示で負荷を抑える (§5.2「縮小表示でよい」)。 */
export const DEFAULT_PREVIEW_MAX_EDGE = 240;

/**
 * 始点プレビュー用の仮の plan 長さ (秒)。実際に生成される動画の尺/fps とは
 * **一致させない** (= モーダルの品質設定を previewFrame に持ち込まない、
 * 独立性を保つ) — frame 0 の cutoff がおおよそ「seed 済み + 実描画はごく僅か」
 * になる程度の密度があれば十分 (要件は背景色/leading-run の正しさであって、
 * 動画の frame 0 とバイト単位で一致することではない)。
 */
const START_PREVIEW_DURATION_SECONDS = 30;

export type TimelapsePreviewVariant = 'start' | 'end';

export interface RenderTimelapsePreviewOptions {
  /**
   * **全量ログ** (merge 済み、昇順 seq) — 動画生成 (`generateTimelapseVideo` /
   * `TimelapseShareModal.handleCreate`) に渡すのと同じ列。モーダルの仮想列
   * (`domain.events`) ではない — leading-run / 背景色の継承を
   * `createTimelapseFrameSource` の既定計算にそのまま委ねるため、前文書の
   * 履歴が見える全量ログが必要。
   */
  readonly events: readonly StrokeLogEvent[];
  /** ルームのキャンバス寸法。 */
  readonly canvas: FrameSize;
  /**
   * 現在選択中の範囲 (§4.7 系譜連結ドメイン)。動画生成と同一の
   * `buildTimelapseSeqRanges(domain, startIdx, endIdx)` の結果をそのまま渡す
   * — モーダル側で index ごとに独自の範囲を組み立てない (= 動画と同じ入力)。
   */
  readonly seqRanges: readonly SeqRange[];
  /** 始点 (frame 0 相当) / 終点 (完成形) のどちらを合成するか。 */
  readonly variant: TimelapsePreviewVariant;
  /** プレビュー長辺上限。既定 {@link DEFAULT_PREVIEW_MAX_EDGE}。 */
  readonly maxEdge?: number;
  /**
   * §5.2 自動クロップをプレビューにも反映する (修正2)。動画生成と同一の
   * `computeChangedRegionBox` で求めた crop 矩形を渡すと、プレビューも動画と
   * 同じ範囲に寄る。省略 / 全面矩形 = 従来どおり全体プレビュー (crop 無し)。
   * `createTimelapseFrameSource` の `sourceRect` にそのまま流す (canvas 内に
   * 収まる正整数矩形であること = `computeChangedRegionBox` が保証)。
   */
  readonly sourceRect?: CropRect;
  /** 生成前後で降りていれば結果を捨てる (stale プレビュー抑止)。 */
  readonly signal?: AbortSignal;
  /**
   * `whiteboard.imagepaste` resolver (vandalism-recovery-ui-2026-09-27.md
   * 「タイムラプスの imagepaste 再生」節) — pass the SAME resolver instance
   * the caller uses for the finished-video generation request (module
   * header of `imagepasteResolve.ts`: 「片方だけだとプレビューと動画が
   * ずれる」). Omit to keep pastes as a no-op in the preview.
   */
  readonly resolveImagepaste?: ResolveImagepasteFn;
}

/**
 * 選択範囲の始点 (frame 0 相当) / 終点 (完成形) の合成 1枚を返す。範囲が空 /
 * abort 済み / 生成不能は `null` (プレビューは無害に握りつぶす = 本番の動画
 * 生成には一切影響させない)。
 *
 * `variant: 'end'` は `createTimelapseFrameSource` を **単一フレーム plan**
 * (duration 極小秒) で回し、選択範囲を全部適用した合成だけを pull する。
 * `variant: 'start'` は多少長い plan (frameCount > 1) を組むが、生成器から
 * **frame 0 だけ** pull してすぐ止める — leading-run/背景色の計算は
 * `seqRanges` 全体を見て行われる (= 動画と同じ経路) ので、開始位置が文書境界
 * そのものでも正しい (Bug A の再発防止)。
 */
export const renderTimelapsePreviewFrame = async (
  opts: RenderTimelapsePreviewOptions,
): Promise<TimelapsePreviewFrame | null> => {
  if (opts.seqRanges.length === 0 || opts.signal?.aborted) return null;

  const source = createTimelapseFrameSource({
    events: opts.events,
    canvas: opts.canvas,
    seqRanges: opts.seqRanges,
    ...(opts.variant === 'start'
      ? { mode: { kind: 'duration' as const, seconds: START_PREVIEW_DURATION_SECONDS } }
      : { mode: { kind: 'duration' as const, seconds: 1 / DEFAULT_TIMELAPSE_FPS } }),
    fps: DEFAULT_TIMELAPSE_FPS,
    maxEdge: opts.maxEdge ?? DEFAULT_PREVIEW_MAX_EDGE,
    // 自動クロップ (修正2)。省略時は frame source が全面扱いにする。
    ...(opts.sourceRect !== undefined ? { sourceRect: opts.sourceRect } : {}),
    ...(opts.resolveImagepaste !== undefined ? { resolveImagepaste: opts.resolveImagepaste } : {}),
  });

  const gen = source.frames();
  try {
    if (opts.variant === 'start') {
      // frame 0 だけ pull する — 残りのフレームは生成しない (finally の
      // `gen.return()` が replay ループを打ち切る、§5.1「フレームを溜め込まない」
      // と同じ精神で「必要な分だけ pull する」)。
      const first = await gen.next();
      if (first.done || opts.signal?.aborted) return null;
      return {
        width: first.value.width,
        height: first.value.height,
        rgba: first.value.rgba,
      };
    }
    // 'end' — 単一フレーム plan なので唯一の yield が「選択範囲を全部適用した
    // 完成形」。abort チェックのため通常の pull loop を回す (既存の流儀通り)。
    let result: TimelapsePreviewFrame | null = null;
    for (;;) {
      const next = await gen.next();
      if (next.done) break;
      result = { width: next.value.width, height: next.value.height, rgba: next.value.rgba };
      if (opts.signal?.aborted) return null;
    }
    return result;
  } finally {
    await gen.return?.(undefined as never);
  }
};
